import { Head, Link } from '@inertiajs/react'
import { AppShell } from '@/layouts/AppShell'
import { PageHeader } from '@/components/layout/PageHeader'
import { StatCard } from '@/components/cards/StatCard'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'

import type { Breadcrumb } from '@/types/shared'

export type TeamDashboardProps = {
  stats: {
    totalPlayers: number
    activeChatrooms: number
    teamChatrooms: number
    pendingApprovals: number
    coachesCount: number
  }
  recentPlayers: Array<{
    id: string
    name: string
    prefecture?: string | null
    positions: string[]
    updatedAt: string
  }>
  recentChatrooms: Array<{
    id: string
    playerName: string
    counterpartName: string
    lastMessage?: string | null
    lastMessageAt?: string | null
  }>
  recentTeamChatrooms: Array<{
    id: string
    schoolName?: string | null
    schoolCoachName: string
    lastMessage?: string | null
    lastMessageAt?: string | null
  }>
  pendingApprovals: Array<{
    id: string
    name: string
    submittedAt: string
    positions: string[]
  }>
  breadcrumbs: Breadcrumb[]
}

export default function TeamDashboard({ stats, recentPlayers, recentChatrooms, recentTeamChatrooms, pendingApprovals, breadcrumbs }: TeamDashboardProps) {
  return (
    <AppShell>
      <Head title="チームダッシュボード" />
      <PageHeader
        title="チームダッシュボード"
        description="チームの状況をひと目で把握し、選手とのコミュニケーションをスムーズに進めましょう。"
        breadcrumbs={breadcrumbs}
      />

      <section className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="所属選手"
          value={stats.totalPlayers}
          delta={{ value: `${stats.totalPlayers} 名が所属`, trend: 'steady' }}
        />
        <StatCard
          label="アクティブチャット"
          value={stats.activeChatrooms}
          delta={{ value: '最新のコミュニケーション履歴', trend: 'steady' }}
        />
        <StatCard
          label="学校からの問い合わせ"
          value={stats.teamChatrooms}
          delta={{ value: stats.teamChatrooms === 0 ? 'まだ問い合わせはありません' : 'スカウトの相談を確認', trend: 'steady' }}
        />
        <StatCard
          label="承認待ち"
          value={stats.pendingApprovals}
          delta={{ value: stats.pendingApprovals === 0 ? '承認待ちはありません' : 'プロフィールの確認が必要です', trend: stats.pendingApprovals === 0 ? 'up' : 'steady' }}
        />
        <StatCard
          label="登録コーチ"
          value={stats.coachesCount}
          delta={{ value: 'チームに紐づくコーチ数', trend: 'steady' }}
        />
      </section>

      <section className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <header className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">最近更新された選手</h2>
            <Link href="/team/players" className="text-sm text-brand-300 hover:text-brand-100 transition">
              一覧を見る
            </Link>
          </header>
          <div className="mt-6 space-y-4">
            {recentPlayers.length === 0 ? (
              <EmptyState message="最近更新された選手はいません" />
            ) : (
              recentPlayers.map((player) => (
                <Link
                  key={player.id}
                  href={`/team/players/${player.id}`}
                  className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/5 px-4 py-4 transition hover:border-white/20 hover:bg-white/10"
                >
                  <div>
                    <p className="text-sm font-semibold text-white">{player.name}</p>
                    <p className="text-xs text-white/60">
                      {(player.prefecture ?? '所在地未登録') + ' · '}
                      {player.positions.length ? player.positions.join(', ') : 'ポジション未設定'}
                    </p>
                  </div>
                  <time className="text-xs text-white/40">{formatDate(player.updatedAt)}</time>
                </Link>
              ))
            )}
          </div>
        </Card>

        <Card>
          <header className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">承認待ちのプロフィール</h2>
            <Badge variant={pendingApprovals.length ? 'warning' : 'success'}>
              {pendingApprovals.length ? `${pendingApprovals.length} 件` : '0 件'}
            </Badge>
          </header>
          <div className="mt-6 space-y-4">
            {pendingApprovals.length === 0 ? (
              <EmptyState message="承認待ちのリクエストはありません" />
            ) : (
              pendingApprovals.map((player) => (
                <Link
                  key={player.id}
                  href={`/team/profile_approvals/${player.id}`}
                  className="block rounded-2xl border border-white/10 bg-white/5 px-4 py-4 text-sm text-white transition hover:border-white/20 hover:bg-white/10"
                >
                  <p className="font-semibold">{player.name}</p>
                  <p className="text-xs text-white/60">
                    {player.positions.length ? player.positions.join(', ') : 'ポジション未設定'} · {formatDate(player.submittedAt)} 提出
                  </p>
                </Link>
              ))
            )}
          </div>
        </Card>
      </section>

      <section className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <header className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">最近のチャット</h2>
            <Link href="/team/chatrooms" className="text-sm text-brand-300 hover:text-brand-100 transition">
              一覧を見る
            </Link>
          </header>
          <div className="mt-6 space-y-4">
            {recentChatrooms.length === 0 ? (
              <EmptyState message="まだチャット履歴がありません" />
            ) : (
              recentChatrooms.map((chatroom) => (
                <Link
                  key={chatroom.id}
                  href={`/team/chatrooms/${chatroom.id}`}
                  className="block rounded-2xl border border-white/10 bg-white/5 px-4 py-4 text-sm text-white transition hover:border-white/20 hover:bg-white/10"
                >
                  <p className="font-semibold">{chatroom.playerName}</p>
                  <p className="text-xs text-white/60">{chatroom.counterpartName}</p>
                  <p className="mt-2 text-xs text-white/70 line-clamp-2">
                    {chatroom.lastMessage ?? 'メッセージはまだありません'}
                  </p>
                  {chatroom.lastMessageAt ? (
                    <time className="mt-1 block text-[11px] text-white/40">{formatDate(chatroom.lastMessageAt)}</time>
                  ) : null}
                </Link>
              ))
            )}
          </div>
        </Card>

        <Card>
          <header className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">クイックアクション</h2>
          </header>
          <div className="mt-6 grid gap-3">
            <QuickLink href="/team/players" label="所属選手を管理" />
            <QuickLink href="/team/profile_approvals" label="承認リクエストを確認" />
            <QuickLink href="/team/chatrooms" label="チャット一覧に移動" />
            <QuickLink href="/team/team_chatrooms" label="学校からの問い合わせを確認" />
          </div>
        </Card>
      </section>

      <section className="grid grid-cols-1 gap-6">
        <Card>
          <header className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">最近の学校からの問い合わせ</h2>
            <Link href="/team/team_chatrooms" className="text-sm text-brand-300 transition hover:text-brand-100">
              一覧を見る
            </Link>
          </header>
          <div className="mt-6 space-y-4">
            {recentTeamChatrooms.length === 0 ? (
              <EmptyState message="まだ問い合わせはありません" />
            ) : (
              recentTeamChatrooms.map((teamChatroom) => (
                <Link
                  key={teamChatroom.id}
                  href={`/team/team_chatrooms/${teamChatroom.id}`}
                  className="block rounded-2xl border border-white/10 bg-white/5 px-4 py-4 text-sm text-white transition hover:border-white/20 hover:bg-white/10"
                >
                  <p className="font-semibold">{teamChatroom.schoolName ?? '学校未登録'}</p>
                  <p className="text-xs text-white/60">{teamChatroom.schoolCoachName} コーチ</p>
                  <p className="mt-2 line-clamp-2 text-xs text-white/70">
                    {teamChatroom.lastMessage ?? 'メッセージはまだありません'}
                  </p>
                  {teamChatroom.lastMessageAt ? (
                    <time className="mt-1 block text-[11px] text-white/40">{formatDate(teamChatroom.lastMessageAt)}</time>
                  ) : null}
                </Link>
              ))
            )}
          </div>
        </Card>
      </section>
    </AppShell>
  )
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-white/10 bg-white/5 px-6 py-12 text-center text-sm text-white/60">
      <p>{message}</p>
    </div>
  )
}

function QuickLink({ href, label }: { href: string; label: string }) {
  return (
    <Link
      href={href}
      className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white transition hover:border-white/20 hover:bg-white/10"
    >
      {label}
      <span className="text-brand-300">→</span>
    </Link>
  )
}

function formatDate(value: string) {
  return new Date(value).toLocaleString('ja-JP', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
